import { ReactElement, ReactNode, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import AIInstructionForm from './AIInstructionForm'

interface Props {
  instruction: { id: string; text: string }
  /** Shown struck through with an "Ignoriert" chip (organisation instruction ignored here). */
  ignored?: boolean
  isUpdating: boolean
  isDeleting: boolean
  /** Omit to hide the edit button. */
  onUpdate?: (text: string) => Promise<unknown>
  /** Omit to hide the delete button. */
  onDelete?: () => void
  /** Further controls, rendered before edit/delete. */
  actions?: ReactNode
}

/** One KI-Instruktion: its text with edit/delete buttons, or the inline edit form. */
const AIInstructionRow = ({
  instruction,
  ignored = false,
  isUpdating,
  isDeleting,
  onUpdate,
  onDelete,
  actions,
}: Props): ReactElement => {
  const [isEditing, setIsEditing] = useState(false)

  if (isEditing && onUpdate) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }} data-cy="ai-instruction-row">
        <AIInstructionForm
          id={`ai-instruction-edit-${instruction.id}`}
          defaultText={instruction.text}
          submitText="Speichern"
          isLoading={isUpdating}
          onSubmit={async text => {
            try {
              await onUpdate(text)
              setIsEditing(false)
            } catch {
              // The failure is already shown as a snackbar: stay in edit mode.
            }
          }}
          onCancel={() => setIsEditing(false)}
          data-cy="ai-instruction-edit-form"
        />
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-cy="ai-instruction-row">
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              ...(ignored && { textDecoration: 'line-through', color: 'text.disabled' }),
            }}
            data-cy="ai-instruction-row-text"
          >
            {instruction.text}
          </Typography>
        </Box>
        {ignored && <Chip size="small" label="Ignoriert" data-cy="ai-instruction-ignored-chip" />}
        {actions}
        {onUpdate && (
          <Tooltip title="Bearbeiten">
            <IconButton
              size="small"
              onClick={() => setIsEditing(true)}
              data-cy="ai-instruction-edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Löschen">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={isDeleting}
                data-cy="ai-instruction-delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  )
}

export default AIInstructionRow
